import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ContentCopy } from '@mui/icons-material';
import { PaymentsStatusEnum } from 'src/shared/models/enums/paymentsStatus.enum';
import { InvoicePayment } from '../models/invoicePayment';

type PaymentDetailProps = {
  setOpenModal: (openModal: boolean) => void;
  payment: InvoicePayment;
};

export const PaymentDetail = (props: PaymentDetailProps) => {
  const { setOpenModal, payment } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [tooltipText, setTooltipText] = useState<string>(String(t('common:button.copy')));

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(payment.ocr || '');
      if (isMounted()) setTooltipText(t('common:button.copied'));
    } catch (err) {
      if (isMounted()) setTooltipText(t('common:button.copyFailed'));
    }
  };

  return (
    <GenericDialog
      open
      dialogTitle={t('invoice:payments.payment')}
      dialogContent={
        <GridWrapper narrow>
          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.tableHeader.date')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              {DateHelper.getDate(payment.date)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.tableHeader.amount')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              <AmountWithCurrency amount={payment.amount} />
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.tableHeader.invoice')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              {payment.ocr || '—'}{' '}
              {payment.ocr && (
                <Tooltip title={tooltipText} onClose={() => setTooltipText(t('common:button.copy'))}>
                  <IconButton onClick={handleCopyClick}>
                    <ContentCopy fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.tableHeader.source')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              {payment.source}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.verificationNo')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              {payment.verificationNo || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.tableHeader.status')}:
            </Typography>
            {payment.status === PaymentsStatusEnum.Failed ? (
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {t('invoice:payments.statusFailed')}
              </Typography>
            ) : (
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {t('invoice:payments.statusCompleted')}
              </Typography>
            )}
          </Grid>

          <Grid item mb={1} xs={12} sm={4}>
            <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
              {t('invoice:payments.comment')}:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
              {payment.comment}
            </Typography>
          </Grid>
        </GridWrapper>
      }
      onClose={() => setOpenModal(false)}
      onSave={() => setOpenModal(false)}
      maxWidth='md'
      showDialogActions={false}
    />
  );
};
